/** biome-ignore-all lint/a11y/useKeyWithClickEvents: filter chips are supplementary pointer targets; keyboard users clear filters via the reset button */
"use client";

import type { Column } from "@tanstack/react-table";
import { CalendarIcon, XCircle } from "lucide-react";
import * as React from "react";
import type { DateRange } from "react-day-picker";

import { Button } from "../components/button";
import { Calendar } from "../components/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/popover";
import { Separator } from "../components/separator";
import { formatDate } from "../lib/format";

type DateSelection = Date[] | DateRange;

function getIsDateRange(value: DateSelection): value is DateRange {
	return value && typeof value === "object" && !Array.isArray(value);
}

function parseAsDate(timestamp: number | string | undefined): Date | undefined {
	if (!timestamp) return undefined;
	const numericTimestamp =
		typeof timestamp === "string" ? Number(timestamp) : timestamp;
	const date = new Date(numericTimestamp);
	return !Number.isNaN(date.getTime()) ? date : undefined;
}

function parseColumnFilterValue(value: unknown) {
	if (value === null || value === undefined) {
		return [];
	}

	if (Array.isArray(value)) {
		return value.map((item) => {
			if (typeof item === "number" || typeof item === "string") {
				return item;
			}
			return undefined;
		});
	}

	if (typeof value === "string" || typeof value === "number") {
		return [value];
	}

	return [];
}

interface DataTableDateFilterProps<TData> {
	column: Column<TData, unknown>;
	title?: string;
	multiple?: boolean;
}

export function DataTableDateFilter<TData>({
	column,
	title,
	multiple,
}: DataTableDateFilterProps<TData>) {
	// Reads filter state off the mutable column instance. See DataTable.
	"use no memo";

	const columnFilterValue = column.getFilterValue();

	const selectedDates = React.useMemo<DateSelection>(() => {
		if (!columnFilterValue) {
			return multiple ? { from: undefined, to: undefined } : [];
		}

		if (multiple) {
			const timestamps = parseColumnFilterValue(columnFilterValue);
			return {
				from: parseAsDate(timestamps[0]),
				to: parseAsDate(timestamps[1]),
			};
		}

		const timestamps = parseColumnFilterValue(columnFilterValue);
		const date = parseAsDate(timestamps[0]);
		return date ? [date] : [];
	}, [columnFilterValue, multiple]);

	const onSelect = React.useCallback(
		(date: Date | DateRange | undefined) => {
			if (!date) {
				column.setFilterValue(undefined);
				return;
			}

			if (multiple && !("getTime" in date)) {
				const from = date.from?.getTime();
				const to = date.to?.getTime();
				column.setFilterValue(from || to ? [from, to] : undefined);
			} else if (!multiple && "getTime" in date) {
				column.setFilterValue(date.getTime());
			}
		},
		[column, multiple],
	);

	const onReset = React.useCallback(
		(event: React.MouseEvent) => {
			event.stopPropagation();
			column.setFilterValue(undefined);
		},
		[column],
	);

	const hasValue = React.useMemo(() => {
		if (multiple) {
			if (!getIsDateRange(selectedDates)) return false;
			return selectedDates.from || selectedDates.to;
		}
		if (!Array.isArray(selectedDates)) return false;
		return selectedDates.length > 0;
	}, [multiple, selectedDates]);

	const formatDateRange = React.useCallback((range: DateRange) => {
		if (!range.from && !range.to) return "";
		if (range.from && range.to) {
			return `${formatDate(range.from)} - ${formatDate(range.to)}`;
		}
		return formatDate(range.from ?? range.to);
	}, []);

	const label = React.useMemo(() => {
		if (multiple) {
			if (!getIsDateRange(selectedDates)) return null;

			const hasSelectedDates = selectedDates.from || selectedDates.to;
			const dateText = hasSelectedDates
				? formatDateRange(selectedDates)
				: "Select date range";

			return (
				<span className="flex items-center gap-2">
					<span>{title}</span>
					{hasSelectedDates && (
						<>
							<Separator
								orientation="vertical"
								className="mx-0.5 data-[orientation=vertical]:h-4"
							/>
							<span>{dateText}</span>
						</>
					)}
				</span>
			);
		}

		if (getIsDateRange(selectedDates)) return null;

		const hasSelectedDate = selectedDates.length > 0;
		const dateText = hasSelectedDate
			? formatDate(selectedDates[0])
			: "Select date";

		return (
			<span className="flex items-center gap-2">
				<span>{title}</span>
				{hasSelectedDate && (
					<>
						<Separator
							orientation="vertical"
							className="mx-0.5 data-[orientation=vertical]:h-4"
						/>
						<span>{dateText}</span>
					</>
				)}
			</span>
		);
	}, [selectedDates, multiple, formatDateRange, title]);

	return (
		<Popover>
			{/* See data-table-faceted-filter: `asChild`, and no nested clear button,
			    because a button inside a button will not hydrate. Clearing moved into
			    the popover below, where it is a sibling rather than a child. */}
			<PopoverTrigger asChild>
				<Button variant="outline" size="sm" className="border-dashed">
					{hasValue ? (
						<XCircle aria-hidden="true" />
					) : (
						<CalendarIcon aria-hidden="true" />
					)}
					{label}
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-auto p-0" align="start">
				{multiple ? (
					<Calendar
						autoFocus
						mode="range"
						selected={
							getIsDateRange(selectedDates)
								? selectedDates
								: { from: undefined, to: undefined }
						}
						onSelect={onSelect}
					/>
				) : (
					<Calendar
						autoFocus
						mode="single"
						selected={
							!getIsDateRange(selectedDates) ? selectedDates[0] : undefined
						}
						onSelect={onSelect}
					/>
				)}
				{hasValue ? (
					<div className="border-t p-1">
						<Button
							variant="ghost"
							size="sm"
							className="w-full"
							onClick={onReset}
						>
							Clear {title ? title.toLowerCase() : "dates"}
						</Button>
					</div>
				) : null}
			</PopoverContent>
		</Popover>
	);
}
